import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import * as Bixby from '$/core';
import * as Services from '$/services';
import BixButton from '$/components/global/bixButton';
import { ReactComponent as DocumentIcon } from '$/assets/icons/DocumentWithTextSecondary.svg';
import { ReactComponent as EyeIcon } from '$/assets/icons/EyeIcon.svg';
import { ReactComponent as PortalLinkIcon } from '$/assets/icons/PortalLinkIcon.svg';
import { ReactComponent as ChevronIcon } from '$/assets/icons/Chevron.svg';
import { ReactComponent as FunnelIcon } from '$/assets/icons/Funnel.svg';
import HeadingBlock from '$/components/global/heading';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import { toastError, toastSuccess } from '$/store/toast/actions';
import './index.scss';
import { WorkflowManagementTask } from '$/core/models';
import {
  WorkflowManagementCommonState,
  WorkflowManagementStatus,
  WorkflowManagementUploadToPortalState,
} from '$/core/enums';

interface IWorkflowManagementProps {
  assigneeList?: Bixby.Models.IIdentifiable[];
}

const WorkflowManagement: React.FC<IWorkflowManagementProps> = props => {
  const { assigneeList } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [emailTrackingTasks, setEmailTrackingTasks] = useState<WorkflowManagementTask[]>([]);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('All');
  const [selectedAssignee, setSelectedAssignee] = useState<string>('All Assignees');

  useEffect(() => {
    const fetchEmailTrackingData = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(undefined);
        const data = await Services.DataSiteService.getEmailTracking();
        setEmailTrackingTasks(data);
      } catch (error) {
        setErrorMessage('Failed to load email tracking data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmailTrackingData();
  }, []);

  // Filter tasks based on selected status
  const filteredTasks =
    selectedStatusFilter === 'All'
      ? emailTrackingTasks
      : emailTrackingTasks.filter(task => task.status === selectedStatusFilter);

  const statusCounts = {
    all: emailTrackingTasks.length,
    [WorkflowManagementStatus.Received]: emailTrackingTasks.filter(t => t.status === WorkflowManagementStatus.Received)
      .length,
    [WorkflowManagementStatus.PendingReview]: emailTrackingTasks.filter(
      t => t.status === WorkflowManagementStatus.PendingReview
    ).length,
    [WorkflowManagementStatus.InProgress]: emailTrackingTasks.filter(
      t => t.status === WorkflowManagementStatus.InProgress
    ).length,
    [WorkflowManagementStatus.DocsUploaded]: emailTrackingTasks.filter(
      t => t.status === WorkflowManagementStatus.DocsUploaded
    ).length,
    [WorkflowManagementStatus.ContentPublished]: emailTrackingTasks.filter(
      t => t.status === WorkflowManagementStatus.ContentPublished
    ).length,
    [WorkflowManagementStatus.OpsReview]: emailTrackingTasks.filter(
      t => t.status === WorkflowManagementStatus.OpsReview
    ).length,
    [WorkflowManagementStatus.Complete]: emailTrackingTasks.filter(t => t.status === WorkflowManagementStatus.Complete)
      .length,
  };

  const statusTabs = [
    { label: 'All', value: 'All' },
    ...Object.values(WorkflowManagementStatus)
      .filter(status => statusCounts[status] >= 1)
      .map(status => ({
        label: status.replace(/([A-Z])/g, ' $1').trim(),
        value: status,
      })),
  ];

  const getActiveTabCount = () => {
    if (selectedStatusFilter === 'All') {
      return statusCounts.all;
    }
    return statusCounts[selectedStatusFilter] || 0;
  };

  const getContextMenuItems = (params: any) => {
    if (params.column.colId === 'name') {
      return [];
    }

    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  const linkCellFormatter = (params: any) => {
    const url = `/admin/company/${params.data['companyId']}/document-hosting/upload/${params.data['emailId'] ?? ''}`;
    const hasMissingClients = params.data.missingClients && params.data.missingClients.length > 0;

    return (
      <div className="workflow-management-view-portal">
        <PortalLinkIcon />
        {hasMissingClients ? (
          <span style={{ cursor: 'not-allowed', opacity: 0.5 }}>Upload To Portal</span>
        ) : (
          <a href={url}>Upload To Portal</a>
        )}
      </div>
    );
  };

  const OpenAdminControlsPopup = () => {
    // need to implement
  };

  if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <div className="workflow_management_container">
        <div className="workflow_management_header">
          <HeadingBlock title={'Workflow Management Tool'} />
          <div className="workflow_management_admin_control">
            <BixButton isLoading={isUpdating} disabled={isUpdating} onClick={OpenAdminControlsPopup}>
              Admin Controls
            </BixButton>
          </div>
        </div>
        <div className="workflow_management_cards">
          <div className="workflow_management_card">
            <span>Received</span>
            <span className="workflow_management_secondary_color">
              {statusCounts[WorkflowManagementStatus.Received]}
            </span>
          </div>
          <div className="workflow_management_card">
            <span>Pending Review</span>
            <span className="workflow_management_secondary_color">
              {statusCounts[WorkflowManagementStatus.PendingReview]}
            </span>
          </div>
          <div className="workflow_management_card">
            <span>In Progress</span>
            <span className="workflow_management_secondary_color">
              {statusCounts[WorkflowManagementStatus.InProgress]}
            </span>
          </div>
          <div className="workflow_management_card">
            <span>Ops Review</span>
            <span className="workflow_management_secondary_color">
              {statusCounts[WorkflowManagementStatus.OpsReview]}
            </span>
          </div>
          <div className="workflow_management_card">
            <span>Complete</span>
            <span className="workflow_management_secondary_color">
              {statusCounts[WorkflowManagementStatus.Complete]}
            </span>
          </div>
        </div>
        <div className="workflow_management_tasks_container">
          <div className="workflow_management_tasks_summary">
            <div className="workflow_management_tasks_summary_left">
              <h3>Doc Upload Tasks</h3>
              <span>
                {emailTrackingTasks.length} {emailTrackingTasks.length === 1 ? 'task' : 'tasks'}
              </span>
            </div>
            <div className="workflow_management_toolbar">
              <div className="workflow_management_filter_icon">
                <FunnelIcon />
              </div>
              <select
                className="workflow_management_assignee_dropdown"
                value={selectedAssignee}
                onChange={e => setSelectedAssignee(e.target.value)}>
                <option value="All Assignees">All Assignees</option>
                {assigneeList?.map(assignee => (
                  <option key={assignee.id} value={assignee.id}>
                    {assignee.id}
                  </option>
                ))}
              </select>
              <div className="workflow_management_status_tabs">
                {statusTabs.map(tab => (
                  <button
                    key={tab.value}
                    className={`workflow_management_status_tab ${selectedStatusFilter === tab.value ? 'active' : ''}`}
                    onClick={() => setSelectedStatusFilter(tab.value)}>
                    {tab.label}
                    {selectedStatusFilter === tab.value && (
                      <span className="workflow_management_badge">{getActiveTabCount()}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {emailTrackingTasks && !Bixby.Utils.isNullOrEmpty(emailTrackingTasks) ? (
            <section className="workflow_management_tasks_list">
              <div className="workflow_management_data_grid">
                <div className="grid_container">
                  <div className="grid ag-theme-alpine">
                    <AgGridReact
                      enableRangeSelection={true}
                      getContextMenuItems={getContextMenuItems}
                      defaultColDef={{
                        flex: 1,
                        minWidth: 200,
                        sortable: true,
                        filter: 'agTextColumnFilter',
                        cellClass: 'align-left',
                      }}
                      rowData={filteredTasks}
                      suppressDragLeaveHidesColumns={true}
                      suppressMovableColumns={true}
                      animateRows={true}
                      rowHeight={35}
                      stopEditingWhenCellsLoseFocus={true}
                      headerHeight={35}
                      columnDefs={[
                        {
                          field: 'clientName',
                          headerName: 'Client Name',
                        },
                        {
                          field: 'dataSiteName',
                          headerName: 'Data Site Name',
                          cellClass: 'align-left',
                        },
                        { field: 'bixbyId', headerName: 'Bixby Id', sort: 'asc' },
                        {
                          field: 'provider',
                          headerName: 'Provider',
                          minWidth: 110,
                        },
                        {
                          field: 'docCount',
                          headerName: 'Doc Count',
                          minWidth: 150,
                          cellDataType: 'number',
                          cellClass: 'align-center',
                          hide: true, // hiding this column as per current requirements, can be enabled when needed
                        },
                        {
                          field: 'docNames',
                          headerName: 'Documents',
                          autoHeight: true,
                          cellRenderer: DocsCellRenderer,
                        },
                        {
                          field: 'newsType',
                          headerName: 'News Type',
                          minWidth: 120,
                        },
                        {
                          field: 'receivedClients',
                          headerName: 'Received Clients',
                          width: 100,
                          autoHeight: true,
                          cellRenderer: ReceivedClientsCellRenderer,
                        },
                        {
                          field: 'missingClients',
                          headerName: 'Missing Clients',
                          width: 100,
                          autoHeight: true,
                          cellRenderer: MissingClientsCellRenderer,
                        },
                        {
                          field: 'emailReceivedDate',
                          headerName: 'Email Received',
                          minWidth: 120,
                          autoHeight: true,
                          cellDataType: 'dateTime',
                          cellRenderer: (params: any) => {
                            const date = new Date(params.value);
                            const dateStr = date.toLocaleDateString('en-US', {
                              month: 'numeric',
                              day: 'numeric',
                              year: 'numeric',
                            });
                            const timeStr = date.toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                            });
                            return (
                              <div
                                style={{
                                  lineHeight: '1.2',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  height: '100%',
                                }}>
                                <div>{dateStr}</div>
                                <div style={{ fontSize: '0.85em' }}>{timeStr}</div>
                              </div>
                            );
                          },
                        },
                        {
                          field: 'portalLink',
                          headerName: 'Upload To Portal',
                          width: 100,
                          cellRenderer: linkCellFormatter,
                        },
                        {
                          field: 'assignee',
                          headerName: 'Assignee',
                          width: 100,
                          editable: true,
                          floatingFilter: true,
                          cellEditor: 'agSelectCellEditor',
                          cellEditorParams: {
                            values: assigneeList?.map(_ => _.id) || [],
                          },
                          hide: true, // hiding this column as per current requirements, can be enabled when needed
                        },
                        {
                          field: 'assignTask',
                          headerName: 'Assign Task',
                          width: 100,
                          hide: true, // hiding this column as per current requirements, can be enabled when needed
                        },
                        {
                          field: 'uploadToPortal',
                          headerName: 'Upload To Portal',
                          width: 100,
                          editable: true,
                          cellEditor: 'agSelectCellEditor',
                          cellEditorParams: {
                            values: Object.values(WorkflowManagementUploadToPortalState),
                          },
                          hide: true, // hiding this column as per current requirements, can be enabled when needed
                        },
                        {
                          field: 'transcription',
                          headerName: 'Transcription',
                          width: 100,
                          editable: true,
                          cellEditor: 'agSelectCellEditor',
                          cellEditorParams: {
                            values: Object.values(WorkflowManagementCommonState),
                          },
                          hide: true, // hiding this column as per current requirements, can be enabled when needed
                        },
                        {
                          field: 'modelUpdate',
                          headerName: 'Model Update',
                          width: 100,
                          editable: true,
                          cellEditor: 'agSelectCellEditor',
                          cellEditorParams: {
                            values: Object.values(WorkflowManagementCommonState),
                          },
                          hide: true, // hiding this column as per current requirements, can be enabled when needed
                        },
                        {
                          field: 'portalLink',
                          headerName: 'Portal Link',
                          width: 100,
                          cellRenderer: (params: any) => {
                            return (
                              <div className="workflow-management-view-portal">
                                <PortalLinkIcon />
                                <a href={params.value}>View Portal</a>
                              </div>
                            );
                          },
                          hide: true, // hiding this column as per current requirements, can be enabled when needed
                        },
                        {
                          field: 'status',
                          headerName: 'Status',
                          width: 100,
                          editable: true,
                          cellEditor: 'agSelectCellEditor',
                          floatingFilter: true,
                          cellEditorParams: {
                            values: Object.values(WorkflowManagementStatus),
                          },
                          hide: true, // hiding this column as per current requirements, can be enabled when needed
                        },
                        {
                          field: 'completedAt',
                          headerName: 'Completed At',
                          width: 100,
                          hide: true, // hiding this column as per current requirements, can be enabled when needed
                        },
                      ]}></AgGridReact>
                  </div>
                </div>
              </div>
            </section>
          ) : (
            <>
              {errorMessage && <div>{errorMessage}</div>}
              <div className="workflow_management_no_data">
                <NoDataFound dataType="tasks" />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
};

function DocsCellRenderer({ value }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      if (triggerRef.current && popupRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const popupHeight = popupRef.current.offsetHeight;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Position above if there's not enough space below
        const shouldPositionAbove = spaceBelow < popupHeight && spaceAbove > spaceBelow;

        setPopupPosition({
          top: shouldPositionAbove ? rect.top - popupHeight : rect.bottom,
          left: rect.left,
        });
      }
    };

    if (isExpanded) {
      // Small delay to ensure popup is rendered before measuring
      setTimeout(updatePosition, 0);
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isExpanded]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        triggerRef.current &&
        popupRef?.current &&
        !triggerRef.current.contains(event.target) &&
        !popupRef.current?.contains(event.target)
      )
        setIsExpanded(false);
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.addEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!value?.length) return null;

  if (value.length <= 1) {
    return (
      <div className="workflow-management-docs-list">
        {value.map(doc => {
          return (
            <div key={`doc-${doc}`} className="workflow-management-doc-item">
              <span className="workflow-management-doc-icon">
                <DocumentIcon />
              </span>
              <div>{doc}</div>
              <span className="workflow-management-doc-icon">
                <EyeIcon />
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  const popupMenu = isExpanded ? (
    <div
      ref={popupRef}
      className="workflow-management-popup-menu"
      style={{ top: `${popupPosition.top}px`, left: `${popupPosition.left}px` }}>
      <div className="workflow-management-popup-header">Documents</div>
      <div className="workflow-management-popup-content">
        {value.map(doc => {
          return (
            <div key={`doc-${doc}`} className="workflow-management-doc-item">
              <span className="workflow-management-doc-icon">
                <DocumentIcon />
              </span>
              <div>{doc}</div>
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="workflow-management-dropdown-wrapper">
        <div
          ref={triggerRef}
          className="workflow-management-dropdown-trigger"
          onClick={() => setIsExpanded(!isExpanded)}>
          <div className="workflow-management-doc-item workflow-management-dropdown-pill">
            <span className="workflow-management-doc-icon">
              <DocumentIcon />
            </span>
            <div>{value.length} documents</div>
            <ChevronIcon className={isExpanded ? 'chevron-up' : 'chevron-down'} />
          </div>
          <span className="workflow-management-doc-icon">
            <EyeIcon />
          </span>
        </div>
      </div>
      {popupMenu && createPortal(popupMenu, document.body)}
    </>
  );
}

function ReceivedClientsCellRenderer({ value }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      if (triggerRef.current && popupRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const popupHeight = popupRef.current.offsetHeight;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Position above if there's not enough space below
        const shouldPositionAbove = spaceBelow < popupHeight && spaceAbove > spaceBelow;

        setPopupPosition({
          top: shouldPositionAbove ? rect.top - popupHeight : rect.bottom,
          left: rect.left,
        });
      }
    };

    if (isExpanded) {
      // Small delay to ensure popup is rendered before measuring
      setTimeout(updatePosition, 0);
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isExpanded]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        triggerRef.current &&
        popupRef?.current &&
        !triggerRef.current.contains(event.target) &&
        !popupRef.current?.contains(event.target)
      )
        setIsExpanded(false);
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.addEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!value?.length) return null;

  if (value.length <= 1) {
    return (
      <div className="workflow-management-clients">
        {value.map(client => {
          return (
            <span key={`recieved-client-${client}`} className="workflow-management-recieved-client">
              {client}
            </span>
          );
        })}
      </div>
    );
  }

  const popupMenu = isExpanded ? (
    <div
      ref={popupRef}
      className="workflow-management-popup-menu"
      style={{ top: `${popupPosition.top}px`, left: `${popupPosition.left}px` }}>
      <div className="workflow-management-popup-header">Received Clients</div>
      <div className="workflow-management-popup-content">
        {value.map(client => {
          return (
            <span key={`recieved-client-${client}`} className="workflow-management-recieved-client">
              {client}
            </span>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="workflow-management-dropdown-wrapper">
        <div
          ref={triggerRef}
          className="workflow-management-dropdown-trigger"
          onClick={() => setIsExpanded(!isExpanded)}>
          <span className="workflow-management-recieved-client workflow-management-dropdown-pill">
            {value.length} clients
            <ChevronIcon className={isExpanded ? 'chevron-up' : 'chevron-down'} />
          </span>
        </div>
      </div>
      {popupMenu && createPortal(popupMenu, document.body)}
    </>
  );
}

function MissingClientsCellRenderer({ value }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      if (triggerRef.current && popupRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const popupHeight = popupRef.current.offsetHeight;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Position above if there's not enough space below
        const shouldPositionAbove = spaceBelow < popupHeight && spaceAbove > spaceBelow;

        setPopupPosition({
          top: shouldPositionAbove ? rect.top - popupHeight : rect.bottom,
          left: rect.left,
        });
      }
    };

    if (isExpanded) {
      // Small delay to ensure popup is rendered before measuring
      setTimeout(updatePosition, 0);
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isExpanded]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        triggerRef.current &&
        popupRef?.current &&
        !triggerRef.current.contains(event.target) &&
        !popupRef.current?.contains(event.target)
      )
        setIsExpanded(false);
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.addEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!value?.length) return null;

  if (value.length <= 1) {
    return (
      <div className="workflow-management-clients">
        {value.map(client => {
          return (
            <span key={`missing-client-${client}`} className="workflow-management-missing-client">
              {client}
            </span>
          );
        })}
      </div>
    );
  }

  const popupMenu = isExpanded ? (
    <div
      ref={popupRef}
      className="workflow-management-popup-menu"
      style={{ top: `${popupPosition.top}px`, left: `${popupPosition.left}px` }}>
      <div className="workflow-management-popup-header">Missing Clients</div>
      <div className="workflow-management-popup-content">
        {value.map(client => {
          return (
            <span key={`missing-client-${client}`} className="workflow-management-missing-client">
              {client}
            </span>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="workflow-management-dropdown-wrapper">
        <div
          ref={triggerRef}
          className="workflow-management-dropdown-trigger"
          onClick={() => setIsExpanded(!isExpanded)}>
          <span className="workflow-management-missing-client workflow-management-dropdown-pill">
            {value.length} clients
            <ChevronIcon className={isExpanded ? 'chevron-up' : 'chevron-down'} />
          </span>
        </div>
      </div>
      {popupMenu && createPortal(popupMenu, document.body)}
    </>
  );
}

const mapStateToProps = () => {
  return {};
};
const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastError: (message: string) => dispatch(toastError(message)),
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WorkflowManagement);
